import React from 'react';
import styles from './BacktestProgressBar.module.css';

export type BacktestPhase = 
  | 'planning' 
  | 'loading_data' 
  | 'executing' 
  | 'loading_positions' 
  | 'complete' 
  | 'failed';

export interface BacktestProgressProps {
  progress: number;  // 0-100
  phase: BacktestPhase;
  currentPeriod?: string;  // Only shown during 'executing' phase
  currentSymbol?: string;  // Symbol being processed
  isComplete?: boolean;
  error?: string;
}

/**
 * Visual progress bar for backtest execution
 * Shows animated loading bar with optional date during execution phase
 * No text labels or percentages - clean minimal design
 */
export const BacktestProgressBar: React.FC<BacktestProgressProps> = ({
  progress,
  phase,
  currentPeriod,
  currentSymbol,
  isComplete = false,
  error,
}) => {
  // Show error state
  if (error || phase === 'failed') {
    return (
      <div className={styles.container}>
        <div className={styles.errorBar}>
          <div className={styles.errorIcon}>✕</div>
          <span className={styles.errorText}>Backtest failed</span>
        </div>
      </div>
    );
  }

  // Show completion state
  if (isComplete || phase === 'complete') {
    return (
      <div className={styles.container}>
        <div className={styles.completeBar}>
          <div className={styles.checkIcon}>✓</div>
          <span className={styles.completeText}>Backtest completed</span>
        </div>
      </div>
    );
  }

  // Show progress bar
  return (
    <div className={styles.container}>
      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {/* Show current symbol to the left of the progress bar */}
        {currentSymbol && (
          <div className={styles.symbolLabel}>
            {currentSymbol}
          </div>
        )}

        <div className={styles.progressWrapper}>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
            <div className={styles.progressShimmer} />
          </div>
        </div>
      </div>

      {/* Show current period only during execution phase */}
      {phase === 'executing' && currentPeriod && (
        <div className={styles.periodLabel}>
          {currentPeriod}
        </div>
      )}
    </div>
  );
};

